/* eslint-disable @next/next/no-img-element */
import {
  AuthFill,
  Button,
  Email,
  FirstName,
  LastName,
  MarketingConsent,
  ModelGrid,
  Title,
} from '@components/form/fields'
import { Form, FormStep, FormWrapper } from '@components/form/layout'
import { Nav, StepIndicator } from '@components/ui'
import { useCampaign } from '@contexts/index'
import { yupResolver } from '@hookform/resolvers/yup'
import { ERROR_MESSAGE } from '@utils/constants'
import {
  closeOverlay,
  getModelImage,
  useFormSteps,
} from '@utils/helpers/index-client'
import { getContentFile, getModels, getPageData } from '@utils/helpers/index-server'
import { formEvent } from '@utils/helpers/tracking'
import { BrochureSchema } from '@utils/schemas'
import axios from 'axios'
import type { GetServerSideProps } from 'next'
import { useEffect, useState } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { upperCaseFirst } from '@utils/helpers/stringHelpers'

type FormData = {
  modelImage?: string
  modelName?: string
  fuel?: string[]
  label?: string
  first_name?: string
  last_name?: string
  email?: string
}

const Brochure = ({
  models,
  content,
  tracking,
  settings,
  brand,
  dealer,
  model,
  query,
}) => {
  const campaign = useCampaign()
  const {
    formStep,
    user,
    expandedStep,
    goToStep,
    toggleExpand,
    closeAllExpands,
  } = useFormSteps(1)
  const [formData, setFormData] = useState<FormData>({})
  const [submitResponse, setSubmitResponse] = useState('')

  // Alias for backward compatibility
  const step = goToStep

  const initialValues = {
    title: '',
    first_name: '',
    last_name: '',
    email: '',
    model: '',
    consent: {
      user: false,
    },
    campaign_response_code: '',
    campaign_code: '',
    response_code: '',
    dealer: '',
    dealername: '',
    is_lexus: brand === 'lexus',
  }

  const methods = useForm({
    resolver: yupResolver(BrochureSchema),
    defaultValues: initialValues,
    mode: 'onTouched',
  })

  // Step change handlers
  const handleVehicleChange = () => {
    step(1, true)
    closeAllExpands()
    setFormData((state) => ({
      ...state,
      modelImage: '',
      modelName: '',
      fuel: undefined,
    }))
    setSubmitResponse('')
  }

  const handleDetailsChange = () => {
    step(3, true)
    closeAllExpands()
    setFormData((state) => ({
      ...state,
      first_name: '',
      last_name: '',
      email: '',
    }))
  }

  // Step indicator components using StepIndicator
  const VehicleStep = () => (
    <StepIndicator
      stepNumber={1}
      label={brand === 'lexus' ? 'Car' : 'Vehicle'}
      currentStep={formStep}
      isCompleted={!!formData.modelName}
      isExpanded={expandedStep === 'vehicle'}
      summaryText={
        formData.modelName
          ? `${formData.modelName}${formData.label ? ` ${formData.label}` : ''}`
          : undefined
      }
      onChangeClick={handleVehicleChange}
      onExpandToggle={() => toggleExpand('vehicle')}
      trackingName="brochure-request"
      expandedContent={
        formData.modelName ? (
          <>
            <div className="text-lg font-medium">{formData.modelName}</div>
            {formData.fuel !== undefined &&
              formData.fuel.some(
                (f) =>
                  f.toLowerCase() !== 'petrol' &&
                  f.toLowerCase() !== 'diesel',
              ) && (
                <div
                  className={`${
                    brand === 'toyota' ? 'font-medium text-blue' : ''
                  } uppercase`}
                >
                  {formData.fuel.join(' & ')}
                </div>
              )}
            <img
              className="w-full font-medium mt-4"
              src={formData.modelImage}
              alt={formData.modelName}
            />
          </>
        ) : undefined
      }
    />
  )

  const DetailsStep = () => (
    <StepIndicator
      stepNumber={2}
      label="Details"
      currentStep={formStep}
      isCompleted={!!formData.first_name}
      isExpanded={expandedStep === 'details'}
      summaryText={formData.first_name}
      onChangeClick={handleDetailsChange}
      onExpandToggle={() => toggleExpand('details')}
      trackingName="brochure-request"
      expandedContent={
        formData.first_name ? (
          <>
            <div>{`${formData.first_name} ${formData.last_name}`}</div>
            <div>{formData.email}</div>
          </>
        ) : undefined
      }
    />
  )

  useEffect(() => {
    if (dealer) {
      methods.setValue(
        'dealer',
        dealer?.applications?.NMSC?.externalID
          ? dealer.applications.NMSC.externalID
          : '',
      )
      methods.setValue('dealername', dealer?.name ? dealer.name : '')
    }
  }, [dealer])

  const onSubmit = async (data) => {
    setSubmitResponse('')
    await axios({
      method: 'post',
      url: `${process.env.NEXT_PUBLIC_API}/brochure`,
      headers: {
        'Content-Type': 'application/json',
      },
      data,
    })
      .then(() => {
        formEvent(
          {
            conversion: 1,
            name: tracking.form,
            step: 'form-submit',
            variant: '',
            page: tracking.page,
          },
          brand,
          data.email,
        )
        closeOverlay(
          content.confirmation.title,
          content.confirmation.copy,
          setSubmitResponse,
        )
      })
      .catch(() => {
        formEvent(
          {
            conversion: 0,
            name: tracking.form,
            step: 'form-error',
            variant: '',
            page: tracking.page,
          },
          brand,
        )
        setSubmitResponse(ERROR_MESSAGE)
        throw new Error(ERROR_MESSAGE)
      })
  }

  useEffect(() => {
    if (campaign?.campaign) {
      methods.setValue('campaign_code', campaign.campaign)
    }

    if (campaign?.media) {
      const code = methods.getValues('campaign_code')
      methods.setValue('campaign_code', code + campaign.media)
    }

    if (campaign?.brochure) {
      methods.setValue('campaign_response_code', campaign.brochure)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [campaign])

  useEffect(() => {
    if (model) {
      methods.setValue('model', model.d4t4.id)
      setFormData((state) => {
        return {
          ...state,
          modelImage: getModelImage(
            brand,
            model?.image?.id,
            model?.image?.grade,
            model?.image?.color,
            'webp',
            model?.image?.fallback,
          ),
          modelName: model.name,
          fuel: model?.filters?.fuel,
        }
      })
      if (query?.method === 'email') {
        step(2, false)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [model])

  return (
    <>
      <Nav
        title={content.title}
        logoType={content?.logotype}
        customLogo={content?.customlogo}
        steps={[VehicleStep(), DetailsStep()]}
      />
      <FormWrapper>
        <FormStep
          title={content.car.title}
          step={formStep}
          active={formStep === 1}
          user={user}
          tracking={{
            conversion: 0,
            name: tracking.form,
            step: 'select-model',
            variant: tracking.variant,
            page: tracking.page,
          }}
        >
          <ModelGrid
            type="brochure"
            models={models}
            pricelist={settings?.pricelist_url}
            showFilter={content.car.filter}
            setModel={(selectedModel) => {
              setTimeout(() => {
                window.scrollTo({ top: 0, left: 0, behavior: 'smooth' })
              }, 100)
              methods.setValue('model', selectedModel.d4t4.id)
              methods.setValue(
                'response_code',
                dealer
                  ? selectedModel?.responsecodes?.dealer_email || ''
                  : selectedModel?.responsecodes?.national_email || '',
              )
              setFormData((state) => {
                return {
                  ...state,
                  modelImage: getModelImage(
                    brand,
                    selectedModel?.image?.id,
                    selectedModel?.image?.grade,
                    selectedModel?.image?.color,
                    'webp',
                    selectedModel?.image?.fallback,
                  ),
                  modelName: selectedModel.name,
                  fuel: selectedModel?.filters?.fuel,
                }
              })
              step(2, true)
            }}
          />
        </FormStep>
        <FormStep
          title={content.details.title}
          step={formStep}
          active={formStep === 2}
          user={user}
          tracking={{
            conversion: 0,
            name: tracking.form,
            step: 'contact-details',
            variant: tracking.variant,
            page: tracking.page,
          }}
        >
          <FormProvider {...methods}>
            <Form onSubmit={methods.handleSubmit(onSubmit)}>
              <AuthFill />
              <Title />
              <FirstName />
              <LastName />
              <Email />
              <MarketingConsent />
              <Button submitResponse={submitResponse}>
                {content.details.submit}
              </Button>
            </Form>
          </FormProvider>
        </FormStep>
      </FormWrapper>
    </>
  )
}

export const getServerSideProps: GetServerSideProps = async (context) => {
  const brand = context.locale
  const form = 'brochure'
 const [content, settings] = getContentFile(brand, `forms/${form}`)

  // if no content/disabled - show 404
  if (!content || !content.attributes?.active) {
    return {
      notFound: true,
    }
  }

  // if content property 'hold' or settings property 'maintenance' is true, redirect to holding page
  if (content.attributes?.hold || settings.attributes.maintenance) {
    return {
      redirect: {
        destination: '/maintenance',
        permanent: false,
      },
    }
  }

  return {
    props: {
      models: getModels(brand, form),
      content: content.attributes,
      settings: settings.attributes,
      title: content.attributes.title,
      tracking: {
        page: content.attributes.tracking?.page,
        form: content.attributes.tracking?.form,
        variant: 'default',
        initialStep: 'select-model',
      },
      description: `${upperCaseFirst(
        brand,
      )} Brochures contain everything you need to know about our range of vehicles. Find out more information on our website or get in touch with us directly.`,
      ...(await getPageData(context, form)),
    },
  }
}

export default Brochure
